import { makeAutoObservable } from "mobx";
import { RootStore } from "@intrasync/stores";

type SortDirection = "asc" | "desc";

const JOB_STATUSES = ["awarded", "completed"];
const QUOTE_STATUSES = ["draft", "estimate", "quoted", "revised"];

export default class JobListViewStore {
  searchQuery = "";
  statusFilter = "all";
  sortBy = "jobIdentifier";
  sortDirection: SortDirection = "asc";

  private rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false } as any);
  }

  setSearchQuery(query: string) {
    this.searchQuery = query;
  }

  setStatusFilter(status: string) {
    this.statusFilter = status;
  }

  setSortBy(field: string) {
    if (this.sortBy === field) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
    } else {
      this.sortBy = field;
      this.sortDirection = "asc";
    }
  }

  get activeView(): string {
    return (
      ((this.rootStore as any).routerStore?.queryParams?.view as string) ||
      "jobs"
    );
  }

  setActiveView(view: string) {
    (this.rootStore as any).routerStore.goTo(null, null, {
      ...(this.rootStore as any).routerStore.queryParams,
      view,
    });
  }

  get projects() {
    return (
      ((this.rootStore as any).manholeProjectsStore as any)?.entities || []
    );
  }

  get structures() {
    return (
      ((this.rootStore as any).manholeStructuresStore as any)?.entities || []
    );
  }

  get quotes() {
    return (
      ((this.rootStore as any).manholeQuotesStore as any)?.entities || []
    );
  }

  get filteredByView() {
    const all = this.projects;
    if (this.activeView === "quotes") {
      return all.filter((p: any) => {
        const status = (p.status || "draft").toLowerCase();
        return QUOTE_STATUSES.includes(status);
      });
    }
    // "jobs" view: projects that have been awarded/completed or have a jobIdentifier
    return all.filter((p: any) => {
      const status = (p.status || "draft").toLowerCase();
      return JOB_STATUSES.includes(status) || !!p.jobIdentifier;
    });
  }

  get filteredProjects() {
    let result = this.filteredByView;

    // Status filter
    if (this.statusFilter !== "all") {
      result = result.filter((p: any) => p.status === this.statusFilter);
    }

    // Search filter
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      result = result.filter(
        (p: any) =>
          (p.jobName || "").toLowerCase().includes(query) ||
          (p.jobIdentifier || "").toLowerCase().includes(query) ||
          (p.customerName || "").toLowerCase().includes(query)
      );
    }

    return result;
  }

  get projectsWithStats() {
    return this.filteredProjects.map((project: any) => {
      const projectId = project.id || project.projectId;

      const structureCount = this.structures.filter(
        (s: any) => s.projectId === projectId
      ).length;

      const linkedQuote = this.quotes.find(
        (q: any) =>
          q.manholeProjectId === projectId ||
          q.projectId === projectId
      );

      return {
        ...project,
        structureCount,
        quoteTotal: linkedQuote?.total || 0,
      };
    });
  }

  get sortedProjects() {
    const sorted = [...this.projectsWithStats];
    const { sortBy, sortDirection } = this;

    sorted.sort((a: any, b: any) => {
      let aVal = a[sortBy];
      let bVal = b[sortBy];

      // Handle nulls — push to bottom
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return 1;
      if (bVal == null) return -1;

      // String comparison
      if (typeof aVal === "string" && typeof bVal === "string") {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
        const cmp = aVal.localeCompare(bVal);
        return sortDirection === "asc" ? cmp : -cmp;
      }

      // Numeric / date comparison
      if (aVal > bVal) return sortDirection === "asc" ? 1 : -1;
      if (aVal < bVal) return sortDirection === "asc" ? -1 : 1;
      return 0;
    });

    return sorted;
  }

  get availableStatuses(): string[] {
    if (this.activeView === "quotes") {
      return QUOTE_STATUSES;
    }
    return JOB_STATUSES;
  }

  reset() {
    this.searchQuery = "";
    this.statusFilter = "all";
    this.sortBy = "jobIdentifier";
    this.sortDirection = "asc";
  }
}
